/**
 * @fileoverview PDB entry identifiers.
 *
 * PDB ids come in two forms:
 *   legacy:   a digit followed by three alphanumerics, e.g. "1abc"
 *   extended: "pdb_" followed by eight alphanumerics, e.g. "pdb_00001abc"
 * wwPDB maps every legacy id to an extended id by zero padding ("1abc" -> "pdb_00001abc").
 * Entries issued only an extended id are distributed only in mmCIF format.
 *
 * The canonical form is the uppercase legacy id when the entry has one ("1ABC", also for
 * "pdb_00001abc"), otherwise the lowercase extended id ("pdb_10001abc"). bfd-server uses the
 * same rules (bioleap common/pdb-id.h).
 */

const legacyFormat = /^[0-9][A-Za-z0-9]{3}$/;
const extendedFormat = /^pdb_[A-Za-z0-9]{8}$/i;
const extendedWithLegacyFormat = /^pdb_0000([0-9][A-Za-z0-9]{3})$/i;

/** @param {string} id */
export function isPdbId(id) {
    return typeof id === 'string' && (legacyFormat.test(id) || extendedFormat.test(id));
}

/**
 * The uppercase legacy id for "1abc" or "pdb_00001abc"; '' if the entry has no legacy id.
 * Services such as KLIFS and UniProt know only legacy ids.
 * @param {string} id
 */
export function legacyPdbId(id) {
    if (typeof id !== 'string') return '';
    if (legacyFormat.test(id)) return id.toUpperCase();
    const match = id.match(extendedWithLegacyFormat);
    return match ? match[1].toUpperCase() : '';
}

/**
 * The canonical form of a PDB id (see above); '' if id is not a PDB id.
 * @param {string} id
 */
export function normalizePdbId(id) {
    const legacy = legacyPdbId(id);
    if (legacy || !isPdbId(id)) return legacy;
    return id.toLowerCase();
}

/**
 * The PDB id that begins a name such as a case or residue name, as written, or '' if there is
 * none: "pdb_00001abc_frags" -> "pdb_00001abc", "1abc_bmoc" -> "1abc", "1abcd" -> "".
 * Use this rather than splitting at the first '_', which is part of an extended id.
 * @param {string} name
 */
export function leadingPdbId(name) {
    const match = typeof name === 'string'
        && name.match(/^(pdb_[A-Za-z0-9]{8}|[0-9][A-Za-z0-9]{3})(?![A-Za-z0-9])/i);
    return match ? match[1] : '';
}
